/**
 * React Query hooks for CRM reporting endpoints.
 *
 * Reports are read-only aggregations — modeled as `useQuery` with
 * cached results so a tab switch doesn't re-aggregate.
 */

import { useQuery } from '@tanstack/react-query';
import api, {
  CrmActivityTrendsResult,
  CrmLostDealAnalysisResult,
  CrmPipelineVelocityResult,
  CrmSalesForecastResult,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/ui/use-toast';

export function usePipelineVelocity(pipelineId: string | null) {
  const query = useQuery<CrmPipelineVelocityResult>({
    queryKey: queryKeys.crm.reports.pipelineVelocity(pipelineId ?? '_none'),
    queryFn: () => api.getCrmPipelineVelocity(pipelineId as string),
    enabled: Boolean(pipelineId),
  });

  if (query.isError && !query.isFetching) {
    toast({
      title: 'Error',
      description: 'Failed to load pipeline velocity',
      variant: 'destructive',
    });
  }

  return {
    data: query.data ?? null,
    loading: query.isLoading,
    refetch: () => query.refetch().then(() => undefined),
  };
}

export function useSalesForecast(pipelineId?: string) {
  const query = useQuery<CrmSalesForecastResult>({
    queryKey: queryKeys.crm.reports.forecast(pipelineId),
    queryFn: () => api.getCrmSalesForecast(pipelineId),
  });

  if (query.isError && !query.isFetching) {
    toast({
      title: 'Error',
      description: 'Failed to load sales forecast',
      variant: 'destructive',
    });
  }

  return {
    data: query.data ?? null,
    loading: query.isLoading,
    refetch: () => query.refetch().then(() => undefined),
  };
}

export function useActivityTrends(period: 'week' | 'month' | 'quarter' = 'month') {
  const query = useQuery<CrmActivityTrendsResult>({
    queryKey: queryKeys.crm.reports.activityTrends(period),
    queryFn: () => api.getCrmActivityTrends(period),
  });

  if (query.isError && !query.isFetching) {
    toast({
      title: 'Error',
      description: 'Failed to load activity trends',
      variant: 'destructive',
    });
  }

  return {
    data: query.data ?? null,
    loading: query.isLoading,
    refetch: () => query.refetch().then(() => undefined),
  };
}

export function useLostDealAnalysis(pipelineId?: string) {
  const query = useQuery<CrmLostDealAnalysisResult>({
    queryKey: queryKeys.crm.reports.lostDeals(pipelineId),
    queryFn: () => api.getCrmLostDealAnalysis(pipelineId),
  });

  if (query.isError && !query.isFetching) {
    toast({
      title: 'Error',
      description: 'Failed to load lost deal analysis',
      variant: 'destructive',
    });
  }

  return {
    data: query.data ?? null,
    loading: query.isLoading,
    refetch: () => query.refetch().then(() => undefined),
  };
}
